import { useEffect, useState } from 'react';
import { Users, UserCheck, UserX, Shield, Activity, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600 mt-1">Overview of platform statistics and user management</p>
          </div>
        </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Active Users</p>
              <p className="text-3xl font-bold text-green-600">{stats?.active_users || 0}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Inactive Users</p>
              <p className="text-3xl font-bold text-red-600">{stats?.inactive_users || 0}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Admin Users</p>
              <p className="text-3xl font-bold text-purple-600">{stats?.admin_users || 0}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center space-x-2 mb-4">
          <Activity className="w-5 h-5 text-slate-600" />
          <h2 className="text-xl font-semibold text-slate-900">Recent Activity</h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium text-slate-900">New Users (Last 30 Days)</p>
              <p className="text-sm text-slate-600">Recent registrations</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats?.recent_users || 0}</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a href="/admin/users" className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors">
            <h3 className="font-semibold mb-1">Manage Users</h3>
            <p className="text-sm text-blue-100">Add, edit, or view user dashboards</p>
          </a>
          <a href="/admin/audit-logs" className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors">
            <h3 className="font-semibold mb-1">View Audit Logs</h3>
            <p className="text-sm text-blue-100">Track admin actions</p>
          </a>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
